import React, { useRef, useEffect } from 'react';
import { selector, useRecoilValue } from 'recoil';
import { userAtom } from './session';
import { Card, Text } from '@mantine/core';

interface LineChartProps {
    data: [number, number][];
    X?: [number, number];
    Y?: [number, number];
    w?: number;
    h?: number;
    xLabelFmt?: (x:number) => string;
}

const mapKey = (arr, k: number | string) => arr.map(v => v[k])
const mapMinMax = (arr) => [Math.min(...arr), Math.max(...arr)] as [number, number]
export const LineChart = ({ data, X = mapMinMax(mapKey(data, 0)), Y = mapMinMax(mapKey(data, 1)), w = 300, h = 150, xLabelFmt = v=>v.toString() }: LineChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!canvasRef.current || !data.length) return;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        // Clear and setup
        ctx.clearRect(0, 0, w, h);
        
        // Draw axes
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 1;
        
        // Y-axis
        ctx.beginPath();
        ctx.moveTo(30, 20);
        ctx.lineTo(30, h - 15);
        ctx.stroke();

        // X-axis
        ctx.beginPath();
        ctx.moveTo(25, h - 22);
        ctx.lineTo(w - 5, h - 22);
        ctx.stroke();

        // Axis labels
        ctx.font = '10px Arial';
        ctx.fillStyle = '#333333';

        // Y-axis label (min)
        ctx.textAlign = 'right';
        ctx.fillText(Y[0].toString(), 25, h - 20);
        
        // Y-axis label (max)
        ctx.fillText(Y[1].toString(), 30, 15);

        // X-axis label (min)
        ctx.textAlign = 'left';
        ctx.fillText(xLabelFmt(X[0]), 40, h - 8);

        // X-axis label (max)
        ctx.textAlign = 'right';
        ctx.fillText(xLabelFmt(X[1]), w - 10, h - 25);

        // Draw chart line
        ctx.strokeStyle = '#ddffdd';
        ctx.lineWidth = 2;

        const scaleX = (val: number) => ((val - X[0]) / (X[1] - X[0])) * (w - 60) + 40;
        const scaleY = (val: number) => (h - 30) - ((val - Y[0]) / (Y[1] - Y[0])) * (h - 50);

        // Draw lines
        ctx.beginPath();
        data.forEach(([x, y], i) => {
            const px = scaleX(x);
            const py = scaleY(y);

            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        });

        // Draw points
        ctx.stroke();
        data.forEach(([x, y]) => {
            ctx.fillStyle = 'black';
            ctx.beginPath();
            ctx.arc(scaleX(x), scaleY(y), 3, 0, Math.PI * 2);
            ctx.fill();
        });

    }, [data]);

    return <canvas ref={canvasRef} width={600} height={250} style={{
        border: 'solid 1px black',
        borderRadius: '5px',
    }}/>;
};

const example = () => <LineChart X={[0, 10]} Y={[0, 1]} data={[[2, 0.5], [3, 0.8]]} />
export default LineChart

export const MonitoringE2E = () => {
    const user = useRecoilValue(userAtom)
    if (user && !user.admin) return `ACCESS ERROR`
    const monitoring = useRecoilValue(monitoringAtom)
    const toDay = (n:number) => new Date(n).toISOString().split('T')[0]
    const toTime = (n:number) => {
        const d = new Date(n)
        return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    }
    return <Card withBorder>
        <Text size="xl">Quotes (by day)</Text>
        <LineChart data={monitoring?.quote_day as Array<[number, number]>} xLabelFmt={toDay}/>
        <Text size="xl">Quotes (by minute)</Text>
        <LineChart data={monitoring?.quote_min as Array<[number, number]>} xLabelFmt={toTime}/>

        <Text size="xl">Payments (by day)</Text>
        <LineChart data={monitoring?.payment_day as Array<[number, number]>} xLabelFmt={toDay}/>
        <Text size="xl">Payments (by minute)</Text>
        <LineChart data={monitoring?.payment_min as Array<[number, number]>} xLabelFmt={toTime}/>

        <Text size="xl">Latest Unit Tests:</Text>
        <KVTable
            data={monitoring?.tests}
        />

        <Text size="xl">Latest End-to-End Test:</Text>
        <KVTable
            data={monitoring?.tests}
        />

        

    </Card>
}

export const monitoringAtom = selector<{
    quote_day: Array<[number, number]>,
    quote_min: Array<[number, number]>,
    payment_day: Array<[number, number]>,
    payment_min: Array<[number, number]>,
    tests: Record<string, string>,
}>({
    key: 'monitoring',
    get: async ({ get }) => {
        return await fetch('/api/system/monitoring.json').then(async res => await res.json())
    },
})

const getColor = (value:string) => {
    if(value?.includes('ERROR:')) return '#ffdddd'
    if(value?.includes('SUCCESS:')) return '#ddffdd'
    if(value?.includes('OK:')) return '#ddeeff'
    return '#eeeeee'
}
const KVTable = ({ data, K = "Test", V = "Result" }) => (
    <table>
        <thead>
            <tr>
                <th>{K}</th>
                <th>{V}</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(data).map(([key, value]:[string,string]) => (
                <tr key={key} style={{backgroundColor: getColor(value)}}>
                    <td>{key}</td>
                    <td>{value}</td>
                </tr>
            ))}
        </tbody>
    </table>
);
